import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AUTH_TOKEN_KEY } from "@/utils/auth";
import PdfVViewer from "./PdfVViewer";

const DIALOG_SELECTOR = "[data-pdf-dialog-content]";

/**
 * PDF را در صورت امکان با fetch و هدر Bearer (و کوکی) به‌صورت blob می‌گیرد تا
 * روی موبایل بتوان همان blob را دانلود کرد؛ ناوبری مستقیم به URL معمولاً Authorization ندارد.
 */
export default function PdfViewerComponent({ fileUrl }) {
  const containerRef = useRef(null);
  const [pdfSrc, setPdfSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [availableHeight, setAvailableHeight] = useState(null);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const dialogContent = element.closest(DIALOG_SELECTOR);
    if (!dialogContent) {
      setAvailableHeight(null);
      return;
    }

    const updateHeight = () => {
      const nextHeight = dialogContent.clientHeight;
      setAvailableHeight(nextHeight > 0 ? nextHeight : null);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(dialogContent);
    return () => observer.disconnect();
  }, []);

  const viewerContainerStyle = availableHeight
    ? {
        width: "100%",
        height: `${availableHeight}px`,
        maxHeight: `${availableHeight}px`,
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }
    : {
        width: "100%",
        flex: 1,
        minHeight: 0,
        maxHeight: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      };

  useEffect(() => {
    if (!fileUrl) {
      setPdfSrc(null);
      setErrorMessage("");
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    let objectUrl = null;

    const loadPdf = async () => {
      setIsLoading(true);
      setErrorMessage("");
      setPdfSrc(null);

      try {
        const normalizedUrl = String(fileUrl).trim();
        const isBlobOrData =
          normalizedUrl.startsWith("blob:") || normalizedUrl.startsWith("data:");

        if (isBlobOrData) {
          if (!cancelled) setPdfSrc(normalizedUrl);
          return;
        }

        const isHttp = /^https?:\/\//i.test(normalizedUrl);
        if (!isHttp) {
          if (!cancelled) setPdfSrc(null);
          return;
        }

        /*
         * همیشه با Bearer (و کوکی) می‌گیریم: تب جدید/iframe نمی‌توانند Authorization بفرستند؛
         * بدون blob، لینک «باز کردن» روی موبایل اغلب به خطا یا «nopage» می‌خورد.
         * اگر CORS اجازه ندهد، به همان URL مستقیم برمی‌گردیم (رفتار قبلی برای دسکتاپ).
         */
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        const headers = {
          Accept: "application/pdf",
          "Accept-Language": "fa-IR",
        };
        if (token) headers.Authorization = `Bearer ${token}`;

        try {
          const response = await fetch(normalizedUrl, { headers, credentials: "include" });
          if (!response.ok) throw new Error(`pdf_fetch_failed_${response.status}`);
          const pdfBlob = await response.blob();
          if (pdfBlob.size === 0) throw new Error("pdf_empty_blob");
          objectUrl = URL.createObjectURL(pdfBlob);
          if (!cancelled) setPdfSrc(objectUrl);
        } catch {
          if (!cancelled) setPdfSrc(normalizedUrl);
        }
      } catch {
        if (!cancelled)
          setErrorMessage("نمایش فایل PDF انجام نشد. لطفاً دوباره تلاش کنید.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileUrl]);

  if (!fileUrl) {
    return (
      <div
        ref={containerRef}
        className="flex h-full min-h-0 w-full items-center justify-center overflow-hidden p-8 text-center text-muted-foreground"
        style={viewerContainerStyle}
      >
        فایلی برای نمایش وجود ندارد
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        ref={containerRef}
        className="flex min-h-[260px] w-full flex-col items-center justify-center gap-4 overflow-hidden bg-slate-50"
        style={viewerContainerStyle}
      >
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">در حال بارگذاری فایل PDF...</p>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div
        ref={containerRef}
        className="flex h-full min-h-0 w-full items-center justify-center overflow-hidden p-8 text-center text-destructive"
        style={viewerContainerStyle}
      >
        {errorMessage}
      </div>
    );
  }

  if (!pdfSrc) {
    return (
      <div
        ref={containerRef}
        className="flex h-full min-h-0 w-full items-center justify-center overflow-hidden p-8 text-center text-muted-foreground"
        style={viewerContainerStyle}
      >
        آدرس فایل PDF نامعتبر است.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex min-h-0 w-full flex-1 flex-col overflow-hidden"
      style={viewerContainerStyle}
    >
      <div className="min-h-0 flex-1 overflow-hidden">
        <PdfVViewer fileUrl={pdfSrc} downloadFileName="gozaresh-list-asnad.pdf" />
      </div>
    </div>
  );
}
